import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FileDropEntry } from '@app/shared/components/smash-file-drop/file-drop-entry';
import { FileSystemMetadata } from '@app/shared/components/smash-file-drop/interfaces';
import { isSafari } from '@app/shared/helpers/environment';
import { FileReaderError } from '@app/shared/models/fileReaderError';
import { Transfer } from '@app/shared/models/transfer';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import * as fromUploader from '../../../store/reducers';
import { SmashFileDropComponent } from '@app/shared/components/smash-file-drop/smash-file-drop.component';
import { Identity } from '@app/shared/models/identity';
import { first } from 'rxjs/operators';
import * as fromRoot from '../../../../../reducers/index';
import { UploaderStatus } from '@smash-sdk/uploader';
import { RawFileStoreService } from '@app/core/services/raw-file-store.service';

interface FileDescription {
  name: string;
  type: 'Folder' | 'File';
  size: number;
  fileNumber?: number;
  extension?: string;
  errors?: FileReaderError[];
}
@Component({
  selector: 'app-upload-files-box',
  templateUrl: './upload-files-box.component.html',
  styleUrls: ['./upload-files-box.component.scss']
})
export class UploadFilesBoxComponent implements OnInit, OnChanges {

  @Input() files: FileSystemMetadata[];
  @Input() totalToUpload: number;

  @ViewChild(SmashFileDropComponent) smashFileDrop: SmashFileDropComponent;

  transfer$: Observable<Transfer>;
  uploadStatus$: Observable<UploaderStatus>;
  identity$: Observable<Identity>;

  addFilesDropdownToggled = false;
  fileDescriptions: FileDescription[] = [];
  fileDescriptionsDic: Map<string, FileDescription> = new Map<string, FileDescription>();
  isSafari: boolean;
  isDirectory: boolean;
  tooltipFileOptions = {
    placement: 'top',
    showDelay: 300
  };

  constructor(
    private store$: Store<fromRoot.State & fromUploader.State>,
    private rawFileStoreService: RawFileStoreService,
  ) {
    this.transfer$ = this.store$.select(fromUploader.getTransfer);
    this.uploadStatus$ = this.store$.select(fromUploader.getUploadStatus);
    this.identity$ = this.store$.select(fromRoot.getActiveIdentity);
  }

  ngOnInit(): void {
    this.isSafari = isSafari();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.files) {
      this.updateFileList(this.files);
    }
  }

  async dropped(files: FileDropEntry[]) {
    // tslint:disable-next-line:no-shadowed-variable
    const identity = await this.identity$.pipe(first(identity => !!identity)).toPromise();
    const newFiles = await this.rawFileStoreService.setFiles(files, identity);
    this.updateFileList(newFiles);
    this.closeDropdownAddFiles();
  }

  onRemoveFile(file: FileDescription) {
    if (file.type === 'Folder') {
      this.rawFileStoreService.removeFolder(file.name);
    } else {
      this.rawFileStoreService.removeFile(file.name);
    }
    this.fileDescriptionsDic.delete(file.name);
    this.fileDescriptions = Array.from(this.fileDescriptionsDic.values()).sort(a => a.type === 'Folder' ? -1 : 1).sort(a => a?.errors?.length > 0 ? -1 : 1);
  }

  toggleDropdownAddFiles(): void {
    this.addFilesDropdownToggled = !this.addFilesDropdownToggled;
  }

  closeDropdownAddFiles(): void {
    this.addFilesDropdownToggled = false;
  }

  openSelectFiles(target: string) {
    this.isDirectory = target === 'folder' ? true : false;
    setTimeout(() => this.smashFileDrop.openFileSelector(), 0);
  }

  updateFileList(files: FileSystemMetadata[]): void {
    const filteredFiles = files.filter(file => !this.fileDescriptionsDic.has(file.rootFolder) && !this.fileDescriptionsDic.has(file.relativePath));
    if (filteredFiles.length) {
      for (const file of filteredFiles) {
        const name = file.rootFolder ? file.rootFolder : file.relativePath;
        const fileDescriptionFound = this.fileDescriptionsDic.get(name);
        if (fileDescriptionFound) {
          this.fileDescriptionsDic.set(name, {
            ...fileDescriptionFound,
            fileNumber: fileDescriptionFound.fileNumber + 1,
            size: fileDescriptionFound.size + file.size,
            errors: file.error ? [...fileDescriptionFound.errors, file.error] : [...fileDescriptionFound.errors],
          });
        } else {
          const fileDescription: FileDescription = {
            name,
            size: file.size,
            fileNumber: 1,
            type: file.rootFolder ? 'Folder' : 'File',
            extension: file.extension,
            errors: file.error ? [file.error] : [],
          };
          this.fileDescriptionsDic.set(name, fileDescription);
        }
      }
      this.fileDescriptions = Array.from(this.fileDescriptionsDic.values()).sort(a => a.type === 'Folder' ? -1 : 1).sort(a => a?.errors?.length > 0 ? -1 : 1);
    }
  }

  get errorsDetected(): any {
    return this.fileDescriptions.some(fileDescription => fileDescription?.errors?.length > 0);
  }
}
